import { BUILDINGS, getBuilding, LEVELS, nextLevel, shiftInBuilding } from '@full-elevator/content';
import type {
  BuildingDef,
  LevelDef,
  Lift,
  Loadout,
  Passenger,
  PowerName,
  ReplayLog,
  RunSummary,
  UpgradeSystem,
} from '@full-elevator/sim';
import {
  alightFloor,
  bonusPayFor,
  bonusStatus,
  canTakeStairs,
  carsOf,
  formatDuration,
  isBaseLoadout,
  liftOf,
  loadoutForShift,
  MILLI_PER_FLOOR,
  objectiveStatus,
  POWER,
  POWER_NAMES,
  payoutFor,
  planOf,
  powerApplies,
  purchase,
  SERVICE,
  shelf,
  starsFor,
  tierOf,
  UPGRADE_SYSTEMS,
} from '@full-elevator/sim';
import { Application } from 'pixi.js';
import type { Watch } from './audio/events.js';
import { soundEvents, watch } from './audio/events.js';
import { Music } from './audio/music.js';
import { Sound } from './audio/sound.js';
import { trackFor } from './audio/tracks.js';
import type { CoachStep } from './coach.js';
import { COACHED_LEVELS, lessonFor } from './coach.js';
import { collectAll, collectReplay } from './collect.js';
import { floorName, groundOf } from './floorNames.js';
import {
  firstPart,
  GARAGE_LESSON,
  garageLessonDue,
  lessonWords,
  upgradeHint,
} from './garageLesson.js';
import { buildingLift } from './lifts.js';
import { INTRODUCTIONS, introduces, nextToMeet } from './meet.js';
import { pausesWhenHidden } from './phone.js';
import { prefetchRenderer } from './prefetch.js';
import { Session } from './session.js';
import type { SaveData } from './storage.js';
import {
  addReplay,
  load,
  loadoutFor,
  markArchetypesSeen,
  markBuildingSeen,
  recordRun,
  reset,
  runMilestone,
  save,
  withLoadout,
} from './storage.js';
import { COLORS } from './theme.js';
import { clockFor, starsInReach } from './ui/clock.js';
import { Countdown } from './ui/countdown.js';
import { callButton, clear, coinIcon, el, icon } from './ui/dom.js';
import { renderGarage } from './ui/garage.js';
import { bonusText } from './ui/goal.js';
import { goalCard } from './ui/goalCard.js';
import { renderHome } from './ui/home.js';
import { homeModel } from './ui/homeModel.js';
import { closeInfo, infoButton } from './ui/info.js';
import type { Zoom } from './ui/liftDoors.js';
import { LiftDoors, stopsDown, stopsUp } from './ui/liftDoors.js';
import { medalSvg } from './ui/medal.js';
import { explainOutcome, gaveUpWaiting, lossCause, lostIn } from './ui/outcome.js';
import { personSvg } from './ui/personSvg.js';
import type { PowerControl } from './ui/powers.js';
import { buildPowerControl, POWER_DONE, POWER_WORDS, powerBadge } from './ui/powers.js';
import type { BuildingDone } from './ui/results.js';
import { renderResults, stopStarSounds } from './ui/results.js';
import type { Settings } from './ui/settings.js';
import { buildSettings } from './ui/settings.js';
import type { Spotlight } from './ui/spotlight.js';
import { spotlight } from './ui/spotlight.js';
import { setData, setHidden, setShown, setText } from './ui/write.js';
import { LAST_SECONDS_MS } from './view/feedback.js';
import { ShaftView } from './view/shaftView.js';

/** "Office Tower" as "The Office Tower", for a sentence; "The Grand Hotel" as it is. */
function theName(name: string): string {
  return name.startsWith('The ') ? name : `The ${name}`;
}

/**
 * A sitting's id: random, made in memory when the game opens (ADR-0024). Twelve
 * hex digits is plenty to keep a beta's sittings apart, and says nothing else.
 */
function newSittingId(): string {
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Screen flow and input wiring.
 *
 * Deliberately thin: it maps taps to simulation commands and simulation state to
 * pixels. Every rule about what those taps mean lives in @full-elevator/sim.
 */

type Screen = 'menu' | 'game' | 'results' | 'garage';

/**
 * A new building's lift is its own (ADR-0027), which nothing in play said
 * (ADR-0041): the parts fitted before stay in the building they were fitted to.
 */
function ownLiftLine(building: BuildingDef): string {
  const before = BUILDINGS[BUILDINGS.findIndex((b) => b.id === building.id) - 1];
  if (!before) return '';
  return `It has a lift of its own. Your parts stay in the ${before.name.replace(/^The /, '')}: fit this one in the garage.`;
}

/** A chip: an icon and a few words (PLAYTEST_LOG 2026-10-29). */
function chip(iconName: string, text: string, tone?: 'bad'): HTMLElement {
  const node = el('span', { class: 'chip' }, [icon(iconName), text]);
  if (tone) node.dataset.tone = tone;
  return node;
}

/** A building's rule, read off its data, as chips; and that its lift is its own. */
function buildingChips(building: BuildingDef): HTMLElement[] {
  const rules = building.rules;
  const ground = building.groundFloor ?? 0;
  const chips: HTMLElement[] = [];
  if (rules.landingCapacity !== undefined) {
    chips.push(chip('person', `${rules.landingCapacity} a floor`));
    chips.push(chip('lost', `−${SERVICE.abandonPenalty}`, 'bad'));
  }
  if (rules.entrances) chips.push(chip('home', `${rules.entrances.length} ways in`));
  if (rules.interchange)
    chips.push(chip('trip', `change at ${floorName(rules.interchange.floor, ground)}`));
  if (ownLiftLine(building)) chips.push(chip('wrench', 'own lift'));
  return chips;
}

/** The first shift's line: who the player is, and where (identity review, 2026-10-27). */
export const FIRST_SHIFT = 'Your first shift on the Office Tower’s lift.';

export class App {
  private data: SaveData = load();
  private screen: Screen = 'menu';
  private paused = false;
  /** Shifts whose goal has been shown this sitting (ADR-0037): a retry goes straight in. */
  private readonly goalsShown = new Set<string>();
  /** Stopped for a card (ADR-0029) or a lesson (ADR-0030): the clock waits, without the pause menu. */
  private held = false;
  /** The lesson on screen in a guided shift (ADR-0030), if any. */
  private coaching: CoachStep | null = null;
  /** Whoever a newcomer's card is pointing out while it is read (ADR-0036), if one is. */
  private meeting: string | null = null;
  private lastEarned = 0;
  private lastMilestone: 'first-clear' | 'new-best' | null = null;
  /** What the results screen says about the garage, if anything (ADR-0041). */
  private lastNudge: { text: string; lesson: boolean } | null = null;
  /** The first part being fitted together in the garage (ADR-0041), and how far along. */
  private garageLesson: { system: UpgradeSystem; step: 'fit' | 'done' } | null = null;
  /** The lesson on screen, if one is, and the results' wait before it (ADR-0041). */
  private spot: Spotlight | null = null;
  private spotTimer: ReturnType<typeof setTimeout> | null = null;
  /** The last run's bonus goal, for the results screen: met or not, and what it paid. */
  private lastBonus: { text: string; met: boolean; coins: number } | null = null;
  /**
   * This sitting (ADR-0024): a random id for as long as the page is open, and a
   * count of the runs kept in it. Never written to storage, so the next time
   * the game is opened it is a different sitting with a different id.
   */
  /** Sound and vibration (v0.8): confirmations of what the screen already shows. */
  private readonly sound = new Sound();
  /** The music (ADR-0056): the menu's, then each building's, under the sounds. */
  private readonly music = new Music();
  /** The lift into and out of every shift (ADR-0057). */
  private readonly doors = new LiftDoors();
  /** The doors are closed or opening on a shift: its clock waits for them. */
  private entering = false;
  /** The shift last set up, so a retry rides no floors. */
  private lastShift: string | null = null;
  /** What the last frame looked like, so a sound is only ever a change. */
  private soundWatch: Watch | null = null;
  private readonly sitting = {
    id: newSittingId(),
    runs: 0,
    openedAt: performance.now(),
  };
  private session: Session | null = null;
  private view: ShaftView | null = null;
  private pixi: Application | null = null;

  private readonly screens: Record<Screen, HTMLElement>;
  private readonly stage: HTMLElement;
  private readonly menuScreen: HTMLElement;
  /** The building the home screen is showing, when the player paged away from the current one. */
  private viewingBuilding: number | null = null;
  /** Where the garage's Back goes. */
  private garageFrom: 'menu' | 'results' = 'menu';
  /** Whose lift the garage is showing: every building has its own (ADR-0027). */
  private garageBuilding = 'office-tower';
  private readonly hudTime: HTMLElement;
  private readonly hudTimeStat: HTMLElement;
  /** The shift's bonus goal in play (ADR-0040): still on, met or missed. */
  private readonly hudBonus: HTMLElement;
  private readonly hudBonusText: HTMLElement;
  private readonly hudTimeLabel: HTMLElement;
  /** Under the clock on a time-graded level: the stars a win could still bring. */
  private readonly hudPips: HTMLElement;
  private readonly pips: SVGSVGElement[];
  private readonly hudDelivered: HTMLElement;
  /** "score", or the score a shift needs (ADR-0037): the goal kept in sight. */
  private readonly hudScoreLabel: HTMLElement;
  private readonly hudDeliveredLabel: HTMLElement;
  private readonly hudLevel: HTMLElement;
  private readonly hudScore: HTMLElement;
  private readonly hudScoreStat: HTMLElement;
  private readonly closeButton: HTMLButtonElement;
  /** Tapping ahead (ADR-0052): forget the stops. Always there in that mode, dim with none. */
  private readonly clearButton: HTMLButtonElement;
  /** Power-ups (ADR-0055): the meter in the lintel, and the four once it is full. */
  /** Three, two, one, go, once the doors are open (PLAYTEST_LOG 2026-10-30). */
  private readonly countdown = new Countdown((step) => this.sound.count(step));
  private readonly powers: PowerControl = buildPowerControl({
    open: () => this.openPowers(),
    choose: (name) => this.usePower(name),
    later: () => this.closePowers(),
    cancel: () => this.stopPicking(),
  });
  /** The stairs chosen: the next person tapped walks, if they can. */
  private pickingStairs = false;
  /** Choosing a power-up, or who takes the stairs: the shift waits (PLAYTEST_LOG 2026-10-29). */
  private choosingPower = false;
  /** When the last power was used, so its word is said once. */
  private powerSaidAt = -1;
  /** What the bottom-right corner held when last measured. */
  private cornerKey = '';
  private readonly resultsBody: HTMLElement;
  private readonly resultsFoot: HTMLElement;
  private readonly settings: Settings;
  private readonly intro: HTMLElement;
  private readonly introBody: HTMLElement;
  private readonly pauseSheet: HTMLElement;
  private readonly garageBody: HTMLElement;
  /** What scrolls in the garage: what it is, then the shelf. */
  private garageScroll: HTMLElement | null = null;
  private readonly walletTag: HTMLElement;

  constructor(private readonly root: HTMLElement) {
    this.menuScreen = el('div', { class: 'screen home', id: 'screen-menu' });
    this.stage = el('div', { id: 'stage' });
    this.hudTime = el('div', { class: 'value', text: '0.0s' });
    this.hudDelivered = el('div', { class: 'value', text: '0/0' });
    this.hudDeliveredLabel = el('div', { class: 'label', text: 'moved' });
    // Small and unobtrusive, but present: knowing which level you are in should
    // not require pausing the game to find out.
    this.hudLevel = el('div', { class: 'level-tag' });
    this.hudBonusText = el('span', { class: 'hud-bonus-text' });
    this.hudBonus = el('div', { class: 'hud-bonus', 'aria-live': 'polite' }, [
      medalSvg(false),
      this.hudBonusText,
    ]);
    this.hudScore = el('div', { class: 'value', text: '0' });
    this.closeButton = el(
      'button',
      { class: 'icon-button wide', type: 'button', 'aria-label': 'Close the doors' },
      [icon('doors'), el('span', { text: 'Close' })],
    );
    // Select mode only, which the game no longer offers: hidden from the start.
    // It was hidden only once the view existed, so the first Play on mobile
    // data showed it for up to seconds (performance review, 2026-10-26).
    this.closeButton.style.display = 'none';
    this.clearButton = el(
      'button',
      { class: 'icon-button wide clear-stops', type: 'button', 'aria-label': 'Clear the stops' },
      [icon('close'), el('span', { text: 'Clear stops' })],
    );
    this.clearButton.style.display = 'none';
    this.resultsBody = el('div', { class: 'results-body' });
    this.resultsFoot = el('div', { class: 'results-foot' });
    this.settings = this.buildSettings();
    this.introBody = el('div', { class: 'sheet-body intro-body' });
    this.intro = el('div', { class: 'sheet', 'data-open': 'false', id: 'intro' }, [this.introBody]);
    this.pauseSheet = this.buildPause();
    this.garageBody = el('div', { class: 'garage-body' });
    this.walletTag = el('div', { class: 'home-coins garage-coins', role: 'status' });

    // "Points", as the goal card and the stars say: one name (writer review, 2026-10-26).
    this.hudScoreLabel = el('div', { class: 'label', text: 'points' });
    this.hudScoreStat = el('div', { class: 'stat' }, [this.hudScore, this.hudScoreLabel]);

    this.hudTimeLabel = el('div', { class: 'label', text: 'time' });
    this.pips = [icon('star'), icon('star'), icon('star')];
    this.hudPips = el('div', { class: 'pips' }, this.pips);
    this.hudTimeStat = el('div', { class: 'stat clock', 'data-urgency': 'calm' }, [
      this.hudTime,
      this.hudTimeLabel,
      this.hudPips,
    ]);

    this.screens = {
      menu: this.buildMenu(),
      game: this.buildGame(),
      results: el('div', { class: 'screen', id: 'screen-results' }, [
        this.resultsBody,
        this.resultsFoot,
      ]),
      garage: this.buildGarage(),
    };

    this.root.append(
      this.screens.menu,
      this.screens.game,
      this.screens.results,
      this.screens.garage,
      this.settings.sheet,
      this.intro,
      this.pauseSheet,
      this.powers.popup,
      this.doors.root,
    );
    this.show('menu');
    // Closing the page mid-run is leaving it too. `pagehide` is the one event a
    // phone reliably sends on the way out; the upload uses `keepalive` for this.
    window.addEventListener('pagehide', () => this.recordLeft('closed'));
    // A phone locked or switched away from mid-shift comes back to it paused
    // (qa-lead review, 2026-10-26): the sim does not advance while paused.
    document.addEventListener('visibilitychange', () => {
      const hidden = document.visibilityState === 'hidden';
      if (hidden) this.pauseWhenAway();
      this.music.setHidden(hidden);
    });
    // The renderer's modules, fetched while the home screen is read, so the
    // first Play does not wait on the network. Pixi itself is not started.
    prefetchRenderer();
    // Audio may only start from a gesture; every tap is one, and unlocking twice is free.
    this.sound.enabled = this.data.sound;
    this.music.setEnabled(this.data.music);
    document.addEventListener(
      'pointerdown',
      () => {
        this.sound.unlock(this.data.sound || this.data.music);
        this.music.attach(this.sound.audio);
      },
      { passive: true },
    );
  }

  // ---------------------------------------------------------------- building

  private buildMenu(): HTMLElement {
    return this.menuScreen;
  }

  private buildGame(): HTMLElement {
    // Every control lives in the reserved strip at the top, except the one that
    // only exists in select mode. Nothing sits over a floor, a passenger or the
    // car — icons alone would have shrunk the overlap, not removed it.
    // Same menu as pause: leaving mid-run is destructive, so it is confirmed by
    // the same deliberate second tap rather than by a dialog of its own.
    const back = callButton('back', 'Back to the building', () => this.setPaused(true));
    // Pause does the same, so on a narrow phone this one makes room for the power-up's.
    back.classList.add('hud-back');
    const pause = callButton('pause', 'Pause', () => this.setPaused(true));

    // The lintel over a lift's doors: two call buttons, the shift's plaque, and
    // one indicator window for the numbers.
    const hud = el('div', { class: 'hud-overlay' }, [
      back,
      pause,
      this.powers.button,
      this.hudLevel,
      el('div', { class: 'indicator' }, [
        el('div', { class: 'stat' }, [this.hudDelivered, this.hudDeliveredLabel]),
        this.hudScoreStat,
        this.hudTimeStat,
      ]),
    ]);

    this.closeButton.addEventListener('click', () => {
      this.session?.send({ type: 'closeDoors' });
    });

    this.clearButton.addEventListener('click', () => {
      const car = this.view?.selectedCar ?? 0;
      this.session?.send({ type: 'clearStops', ...(car === 0 ? {} : { car }) });
    });

    const controls = el('div', { class: 'controls-overlay' }, [this.closeButton, this.clearButton]);

    this.stage.append(hud, controls, this.hudBonus, this.powers.hint, this.countdown.root);
    return el('div', { class: 'screen', id: 'screen-game' }, [this.stage]);
  }

  /**
   * Paused: the only place a run can be thrown away, and it takes two taps.
   *
   * The same frame as everything around play (ADR-0017's lintel): the shift's
   * plaque on walnut, the run's numbers in the indicator window, Resume as the
   * big button, and the two ways out — both of which end this run — as brass.
   */
  private buildPause(): HTMLElement {
    // The same switch as in Settings, read again each time the sheet opens (setPaused).
    const music = el('button', {
      class: 'switch',
      type: 'button',
      role: 'switch',
      'aria-label': 'Music',
      'aria-checked': 'true',
    });
    music.addEventListener('click', () => {
      const on = music.getAttribute('aria-checked') !== 'true';
      music.setAttribute('aria-checked', String(on));
      this.setMusic(on);
    });
    const resume = el('button', { class: 'action-pill pause-resume', type: 'button' }, [
      icon('play'),
      'Resume',
    ]);
    resume.addEventListener('click', () => {
      this.setPaused(false);
    });

    const restart = el('button', { class: 'brass-pill', type: 'button' }, [
      icon('retry'),
      el('span', { text: 'Restart' }),
    ]);
    restart.addEventListener('click', () => {
      const level = this.session?.level;
      this.recordLeft('restart');
      this.setPaused(false);
      if (level) this.beginLevel(level);
    });

    // "Main menu", the same words wherever the home icon is (the owner, PLAYTEST_LOG
    // 2026-10-30): "Building" was left over and meant nothing to them.
    const quit = el('button', { class: 'brass-pill', type: 'button' }, [
      icon('home'),
      el('span', { text: 'Main menu' }),
    ]);
    quit.addEventListener('click', () => {
      const level = this.session?.level;
      this.recordLeft('quit');
      this.setPaused(false);
      this.endSession();
      this.leaveShift(level);
    });

    const card = el('div', { class: 'pause-card' }, [
      el('header', { class: 'pause-head lintel' }, [
        el('span', { class: 'plaque pause-plaque' }),
        el('div', { class: 'pause-titles' }, [
          el('div', { class: 'pause-kicker', text: 'Paused' }),
          el('div', { class: 'pause-title' }),
        ]),
      ]),
      el('div', { class: 'pause-body' }, [
        el('div', { class: 'pause-stats' }),
        resume,
        el('div', { class: 'pause-row' }, [restart, quit]),
        el('p', { class: 'pause-warn', text: 'Restart and Building end this shift.' }),
        // The music, a tap from the shift (the owner, PLAYTEST_LOG 2026-10-29).
        el('div', { class: 'pause-toggle' }, [icon('music'), el('span', { text: 'Music' }), music]),
      ]),
    ]);

    const sheet = el('div', { class: 'sheet', 'data-open': 'false', id: 'pause' }, [card]);
    // Tapping the dimmed area resumes: the safe outcome is the easy one.
    sheet.addEventListener('click', (event) => {
      if (event.target === sheet) this.setPaused(false);
    });
    return sheet;
  }

  /**
   * Keep a run that was left before it ended (v0.8 Closed Beta).
   *
   * The beta's most telling moments — where someone gave up, or started over —
   * used to leave no trace: a run only saved and uploaded itself on reaching the
   * results screen. Now leaving through the pause menu, or closing the page
   * mid-run, keeps it too, marked with how it ended. Same rules as a finished
   * run: saved locally first, uploaded after, never blocking, never throwing,
   * and nothing about who played. No stars, no coins, no record: it did not end.
   *
   * A second's play is not a decision, so anything shorter is not kept.
   */
  private recordLeft(how: 'quit' | 'restart' | 'closed'): void {
    const session = this.session;
    if (!session || this.screen !== 'game' || session.finished) return;
    if (session.state.elapsedMs < 1000) return;
    const replay: ReplayLog = this.inSitting({
      ...session.toReplay(`${session.level.name} — ${session.mode}`),
      endedBy: how,
    });
    this.data = addReplay(this.data, replay);
    save(this.data);
    collectReplay(replay);
  }

  /**
   * Mark a kept run as the next one of this sitting, and as played from a save
   * that used Developer tools if it was.
   */
  private inSitting(log: ReplayLog): ReplayLog {
    this.sitting.runs += 1;
    return {
      ...log,
      sitting: {
        id: this.sitting.id,
        run: this.sitting.runs,
        atMs: Math.round(performance.now() - this.sitting.openedAt),
      },
      ...(this.data.devTools ? { devTools: true as const } : {}),
    };
  }

  /** The page went out of sight: a shift that was running is paused (ui: phone.ts). */
  private pauseWhenAway(): void {
    const session = this.session;
    const away = {
      screen: this.screen,
      playing: session !== null && !session.finished,
      paused: this.paused,
      held: this.held,
    };
    if (pausesWhenHidden(away)) this.setPaused(true);
  }

  private setPaused(paused: boolean): void {
    if (!this.session && paused) return;
    if (paused && this.powers.isOpen) this.closePowers();
    this.paused = paused;
    this.pauseSheet.dataset.open = String(paused);
    this.music.setDucked(paused);
    this.pauseSheet
      .querySelector('.pause-toggle .switch')
      ?.setAttribute('aria-checked', String(this.data.music));

    const session = this.session;
    if (!paused || !session) return;
    const plaque = this.pauseSheet.querySelector('.pause-plaque');
    const title = this.pauseSheet.querySelector('.pause-title');
    const stats = this.pauseSheet.querySelector<HTMLElement>('.pause-stats');
    if (plaque) plaque.textContent = String(shiftInBuilding(session.level.id)).padStart(2, '0');
    if (title) title.textContent = session.level.name;
    if (!stats) return;
    // The run so far, as the HUD has it right now: what the level grades, then
    // the clock — so the choice to restart is made with the numbers in view.
    const cell = (value: string, label: string): HTMLElement =>
      el('div', { class: 'pause-stat' }, [
        el('div', { class: 'pause-value', text: value }),
        el('div', { class: 'pause-label', text: label }),
      ]);
    stats.replaceChildren(
      cell(this.hudDelivered.textContent ?? '', this.hudDeliveredLabel.textContent ?? ''),
      ...(this.hudScoreStat.style.display === 'none'
        ? []
        : [cell(this.hudScore.textContent ?? '', 'points')]),
      cell(this.hudTime.textContent ?? '', `${this.hudTimeLabel.textContent ?? 'time'} left`),
    );
  }

  /**
   * The garage.
   *
   * Every entry is a named part with a real number and a sentence about what it
   * lets you do — never a percentage. The plan update is explicit that the way
   * this milestone fails is by turning into an idle-game stat sheet, and the
   * defence is that the player can always say what changed and why it matters.
   */
  private buildGarage(): HTMLElement {
    // One band at the top, as on the home screen and in play: Back on a brass
    // call button, the name, and the purse in the indicator window. It was a
    // tall head above and a foot below, and the owner found the two confusing
    // (PLAYTEST_LOG, 2026-10-28). What the garage is scrolls with the shelf.
    const top = el('header', { class: 'garage-top lintel' }, [
      callButton('back', 'Back', () => this.show(this.garageFrom)),
      el('h1', { text: 'Garage' }),
      this.walletTag,
    ]);
    // What the garage is, behind the lift's "i" (PLAYTEST_LOG 2026-10-29).
    this.garageScroll = el('div', { class: 'garage-scroll' }, [this.garageBody]);
    return el('div', { class: 'screen', id: 'screen-garage' }, [top, this.garageScroll]);
  }

  /**
   * Into the garage, from the home screen or straight from a result. Back
   * returns where the player came from: someone who stopped to fit a part
   * between shifts wants Next, not the building.
   */
  private openGarage(from: 'menu' | 'results', buildingId: string | undefined): void {
    this.garageFrom = from;
    this.garageBuilding = buildingId ?? 'office-tower';
    // A results screen shown again would replay its ceremony; it has been seen.
    if (from === 'results') this.resultsBody.dataset.settled = 'true';
    this.show('garage');
  }

  private renderGarage(fitted: UpgradeSystem | null = null): void {
    // Fitting a part redraws the shelf; the page stays where the part is, so its
    // fitting can be seen (it went back to the top: PLAYTEST_LOG, 2026-10-28).
    const scrolled = this.garageScroll?.scrollTop ?? 0;
    this.renderShelf(fitted);
    if (this.garageScroll) this.garageScroll.scrollTop = fitted ? scrolled : 0;
  }

  private renderShelf(fitted: UpgradeSystem | null): void {
    this.walletTag.replaceChildren(coinIcon(), el('span', { text: String(this.data.coins) }));
    this.walletTag.setAttribute('aria-label', `${this.data.coins} coins`);
    const building = getBuilding(this.garageBuilding);
    const lift = buildingLift(building.id);
    const loadout = loadoutFor(this.data, lift.id);
    // The page takes the building's colours, its buttons too, so each lift's
    // parts are fitted in its own place (the owner, PLAYTEST_LOG 2026-10-30).
    this.screens.garage.dataset.building = building.id;
    // The lifts of the buildings reached so far, to switch between; later ones
    // are not shown, as the home screen does not show their floors.
    const reached = homeModel(this.data, LEVELS, BUILDINGS)
      .buildings.filter((b) => b.reached || b.def.id === building.id)
      .map((b) => ({ id: b.def.id, name: b.def.name }));
    // The first part, fitted together (ADR-0041): whenever the garage opens
    // while it is due, by whichever button.
    if (!this.garageLesson && this.lessonDue(loadout, lift)) {
      const system = firstPart(this.data.coins, loadout, lift.parts);
      if (system) this.garageLesson = { system, step: 'fit' };
    }
    const lesson = this.garageLesson;
    renderGarage(
      this.garageBody,
      {
        coins: this.data.coins,
        lift,
        loadout,
        fitted,
        building: building.id,
        reached,
        lesson: lesson ? { system: lesson.system } : null,
      },
      (system) => this.fitPart(system, lift, loadout),
      (buildingId) => {
        this.garageBuilding = buildingId;
        this.renderGarage();
      },
    );
    this.clearSpot();
    if (!lesson) return;
    // The part's Fit, lit and tapped by the hand; once it is on, what it meant.
    const tier = lift.parts[lesson.system][(loadout[lesson.system] ?? 0) + 1];
    const words = lessonWords(lesson.step, tier?.name ?? '', this.data.coins, building.name);
    if (lesson.step === 'fit') {
      // The whole card lit, so what the part does stays readable; Fit is the tap.
      const part = this.garageBody.querySelector<HTMLElement>('.part-card[data-lesson="target"]');
      const fit = part?.querySelector<HTMLElement>('.part-buy') ?? null;
      this.spot = spotlight(fit, words, () => this.fitPart(lesson.system, lift, loadout), part);
    } else {
      this.spot = spotlight(null, words, () => {
        this.garageLesson = null;
        this.renderGarage();
      });
    }
  }

  /** The first part is due (ADR-0041): never taught, nothing fitted, and the motor in reach. */
  private lessonDue(loadout: Loadout, lift: Lift): boolean {
    return garageLessonDue(
      this.data.coached,
      this.data.loadouts,
      this.data.coins,
      loadout,
      lift.parts,
    );
  }

  /**
   * The lesson's first step (ADR-0041): the screen dims but for Upgrade, and
   * the hand taps it. On the results it waits for the stars to land.
   */
  private callToGarage(button: HTMLElement | null, lift: Lift, wait: number): void {
    this.clearSpot();
    const loadout = loadoutFor(this.data, lift.id);
    const system = firstPart(this.data.coins, loadout, lift.parts);
    if (!button || !system) return;
    const tier = lift.parts[system][(loadout[system] ?? 0) + 1];
    const words = lessonWords('call', tier?.name ?? '', this.data.coins, '');
    const show = (): void => {
      this.spotTimer = null;
      this.spot = spotlight(button, words, () => button.click());
    };
    if (wait > 0) this.spotTimer = setTimeout(show, wait);
    else show();
  }

  private clearSpot(): void {
    if (this.spotTimer !== null) clearTimeout(this.spotTimer);
    this.spotTimer = null;
    this.spot?.remove();
    this.spot = null;
  }

  /** A part bought and fitted; during the lesson, the lesson's part only. */
  private fitPart(system: UpgradeSystem, lift: Lift, loadout: Loadout): void {
    const lesson = this.garageLesson;
    if (lesson && (lesson.step !== 'fit' || lesson.system !== system)) return;
    const result = purchase({ coins: this.data.coins, loadout }, system, lift.parts);
    if (!result) return;
    this.data = withLoadout(
      { ...this.data, coins: result.wallet.coins },
      lift.id,
      result.wallet.loadout,
    );
    if (lesson) {
      // Done once the part is on: the card that follows only says what it meant.
      this.data = { ...this.data, coached: [...new Set([...this.data.coached, GARAGE_LESSON])] };
      this.garageLesson = { system, step: 'done' };
    }
    save(this.data);
    this.renderGarage(system);
  }

  /**
   * Settings (ui/settings.ts): the player's own, and the developer tools apart
   * from them. Every action is the save's, so the sheet only says what to do.
   */
  private buildSettings(): Settings {
    return buildSettings({
      view: () => ({
        sound: this.data.sound,
        music: this.data.music,
        tapAhead: this.data.tapAhead === true,
        unlockAll: this.data.unlockAll,
        devTools: this.data.devTools,
        coins: this.data.coins,
        replays: this.data.replays.length,
        stamp: __BUILD_STAMP__,
      }),
      sound: (on) => {
        this.data = { ...this.data, sound: on };
        this.sound.enabled = on;
        this.sound.unlock();
        save(this.data);
      },
      music: (on) => this.setMusic(on),
      tapAhead: (on) => {
        this.data = { ...this.data, tapAhead: on };
        save(this.data);
      },
      // The guided first shifts again (ADR-0030), for someone handing the phone
      // on: the two ways to send the lift are offered again with them.
      guideAgain: () => {
        this.data = { ...this.data, coached: [], tapAhead: null };
        save(this.data);
      },
      // Downloads *and* uploads. The download is the fallback that works with no
      // network; the upload is what saves someone attaching a file by hand.
      sendReplays: async () => {
        this.exportReplays();
        const held = this.data.replays.length;
        const stored = await collectAll(this.data.replays);
        return stored === null
          ? `Downloaded ${held} — sending is unavailable here.`
          : `Sent ${stored} of ${held}, and downloaded a copy.`;
      },
      // A testing affordance, not a game feature: replaying thirteen levels you
      // already understand to reach the one being tested teaches nothing.
      unlockAll: (on) => {
        this.data = { ...this.data, unlockAll: on, devTools: this.data.devTools || on };
        save(this.data);
        this.renderLevels();
      },
      // Levels 21 onward are graded against a lift the player is assumed to have
      // bought (ADR-0011), so the shop has to be reachable when testing them.
      addCoins: () => {
        this.data = { ...this.data, coins: this.data.coins + 500, devTools: true };
        save(this.data);
        this.renderLevels();
      },
      reset: () => {
        reset();
        this.data = load();
        this.sound.enabled = this.data.sound;
        this.music.setEnabled(this.data.music);
        this.renderLevels();
        this.settings.close();
      },
    });
  }

  /** The music on or off, from Settings or the pause sheet (ADR-0056). */
  private setMusic(on: boolean): void {
    this.data = { ...this.data, music: on };
    this.music.setEnabled(on);
    this.sound.unlock(on);
    this.music.attach(this.sound.audio);
    save(this.data);
  }

  // ------------------------------------------------------------------ screens

  private show(screen: Screen): void {
    // A star still to land on a screen nobody is looking at stays silent.
    if (screen !== 'results') stopStarSounds();
    // A lesson left half way is due again next time; one finished is closed.
    if (screen !== 'garage') this.garageLesson = null;
    this.clearSpot();
    this.screen = screen;
    for (const [name, node] of Object.entries(this.screens)) {
      node.dataset.active = String(name === screen);
    }
    // Pixi draws only in play. Behind the menus it drew an empty stage at the
    // screen's rate (performance review, 2026-10-26, problem 4).
    if (this.pixi) {
      if (screen === 'game') this.pixi.ticker.start();
      else this.pixi.ticker.stop();
    }
    // The menu's music off shift; a shift's is chosen as it begins, and plays on
    // through its results (ADR-0056).
    if (screen === 'menu' || screen === 'garage') this.music.play('menu');
    if (screen !== 'game') this.music.setHurry(false);
    if (screen === 'menu') {
      // Coming back to the menu always opens on where the player is.
      this.viewingBuilding = null;
      this.renderLevels();
    }
    if (screen === 'garage') this.renderGarage();
  }

  /**
   * The home screen: the building the player is in (ADR-0019, which replaced
   * ADR-0012's operations board and kept its rule that density is earned — a
   * fresh save shows a title, one lit floor and one button).
   */
  private renderLevels(enter?: 'from-left' | 'from-right'): void {
    const model = homeModel(this.data, LEVELS, BUILDINGS);
    const viewing = this.viewingBuilding ?? model.current;
    // The lift parked in the building on screen: its own (ADR-0027). A
    // building not reached yet is only looked at, so the garage stays with
    // the one the player is in.
    const shown = model.buildings[viewing];
    const viewedId =
      (shown?.reached ? shown : model.buildings[model.current])?.def.id ?? 'office-tower';
    const lift = buildingLift(viewedId);
    const loadout = loadoutFor(this.data, lift.id);
    renderHome(
      this.menuScreen,
      {
        model,
        enter,
        viewing,
        coins: this.data.coins,
        garageReady: shelf({ coins: this.data.coins, loadout }, lift.parts).some(
          (offer) => offer.affordable,
        ),
        lessonDue: this.lessonDue(loadout, lift),
        loadout,
        stamp: __BUILD_STAMP__,
      },
      {
        play: (level) => this.startLevel(level),
        // Arrows, street signs and swipes all page the same way, and the new
        // building arrives from the side it lies on.
        view: (building) => {
          this.viewingBuilding = building;
          this.renderLevels(building > viewing ? 'from-right' : 'from-left');
        },
        garage: () => this.openGarage('menu', viewedId),
        settings: () => this.settings.open(),
      },
    );
    // The first part (ADR-0041), if the results did not get to it: a run left
    // before its end, or the game closed on them.
    const current = model.buildings[model.current]?.def.id;
    if (viewedId === current && this.lessonDue(loadout, lift)) {
      this.callToGarage(this.menuScreen.querySelector<HTMLElement>('.home-upgrade'), lift, 0);
    }
  }

  // -------------------------------------------------------------------- play

  async ensurePixi(): Promise<Application> {
    if (this.pixi) return this.pixi;
    // Canvas text is rasterised once, in whatever face exists at that moment, and
    // a chip label never redraws — so wait for the display face first. Bounded:
    // a font that will not arrive must never stop the game from starting.
    await Promise.race([
      document.fonts.load('700 15px "Baloo 2"').catch(() => []),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]);
    const app = new Application();
    await app.init({
      background: COLORS.background,
      antialias: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
      resizeTo: this.stage,
    });
    this.stage.append(app.canvas);
    this.pixi = app;

    // 60 a second at most: a 90 or 120 Hz phone drew one and a half to two
    // times as often, for no motion a player can see (ADR-0047's frame budget).
    app.ticker.maxFPS = 60;
    app.ticker.add(() => {
      this.frame(app.ticker.deltaMS);
    });
    // Left before the renderer was ready: it waits for the next shift.
    if (this.screen !== 'game') app.ticker.stop();

    // A phone takes the WebGL context away and gives it back, in the background
    // or short of memory. Pixi uploads again what it can, but a text's canvas
    // went back to a pool once uploaded, so every ticket came back blank until
    // the shift ended (performance review, 2026-10-26, problem 1). Pixi's own
    // handler runs first, being added first; then the shift is drawn anew.
    app.canvas.addEventListener('webglcontextrestored', () => this.redrawView());

    const observer = new ResizeObserver(() => {
      this.view?.resize(this.stage.clientWidth, this.stage.clientHeight);
    });
    observer.observe(this.stage);

    return app;
  }

  /**
   * Introduce anything new, then start. Shown before the level rather than
   * during it, so reading never costs the clock the player is graded on.
   */
  startLevel(level: LevelDef): void {
    // The building first: it changes what every passenger on screen means, so
    // meeting it after the traits would be explaining the words before the
    // sentence they are in.
    const building = level.buildingId;
    // Not the first building, on a first launch: the guided shifts introduce it
    // by playing it, and a card about buildings before anyone has seen a lift
    // was the first thing beta 2's newcomers met (ADR-0030).
    if (building !== undefined && building === LEVELS[0]?.buildingId) {
      this.data = markBuildingSeen(this.data, building);
    }
    if (building !== undefined && !(this.data.seenBuildings ?? []).includes(building)) {
      this.showBuildingIntro(getBuilding(building), level);
      return;
    }
    // Two ways to send the lift (ADR-0052), offered once, on the last guided
    // shift: after shift 2 has taught the last tap.
    if (level.id === LEVELS[2]?.id && this.data.tapAhead === null) {
      this.offerTapAhead(level);
      return;
    }
    // What the shift asks, once a sitting (ADR-0037): a retry knows already.
    if (!this.goalsShown.has(level.id)) {
      this.showGoal(level);
      return;
    }
    // Characters are met in play, the moment they first appear (ADR-0036).
    this.beginLevel(level);
  }

  /**
   * The shift's goal, before it starts (ADR-0037): what it asks, in one line
   * under a headline, and what three stars and two take.
   */
  private showGoal(level: LevelDef): void {
    clear(this.introBody);
    // A building's last shift is its finale (the owner asked for a boss fight:
    // PLAYTEST_LOG, 2026-10-28). The card says so; the shift is the hardest of its five.
    const here = level.buildingId ?? 'office-tower';
    const mates = LEVELS.filter((l) => (l.buildingId ?? 'office-tower') === here);
    const finale = mates[mates.length - 1]?.id === level.id;
    this.introBody.append(
      ...goalCard(level, {
        shift: shiftInBuilding(level.id),
        hard: level.hard ? (finale ? 'finale' : 'hard') : null,
        // Who the player is, said once, on the first shift (identity review, 2026-10-27).
        welcome: level.id === LEVELS[0]?.id ? FIRST_SHIFT : null,
        // "Half as much again" can be read as "half" (writer review, 2026-10-26).
        hardLine: level.hard
          ? finale
            ? `${theName(getBuilding(here).name)}'s last shift. It pays 1.5 times the coins.`
            : 'This one is tough, and it pays 1.5 times the coins.'
          : null,
        play: () => {
          this.goalsShown.add(level.id);
          this.closeIntro();
          this.startLevel(level);
        },
        close: () => this.closeIntro(),
      }),
    );
    this.aimGoalFrom(level);
    this.openIntro('goal');
  }

  /**
   * The goal card grows from the floor that was tapped, and goes back into it
   * (PLAYTEST_LOG 2026-10-29); from the results it rises from below instead.
   */
  private aimGoalFrom(level: LevelDef): void {
    const card = this.introBody.style;
    const floor =
      this.screen === 'menu'
        ? this.menuScreen.querySelector<HTMLElement>(`.floor[data-level="${level.id}"]`)
        : null;
    if (!floor) {
      card.removeProperty('--from-x');
      card.removeProperty('--from-y');
      return;
    }
    const box = floor.getBoundingClientRect();
    const frame = this.root.getBoundingClientRect();
    card.setProperty('--from-x', `${box.left + box.width / 2 - (frame.left + frame.width / 2)}px`);
    card.setProperty('--from-y', `${box.top + box.height / 2 - (frame.top + frame.height / 2)}px`);
  }

  /** The card over the screen: a sheet from below, or the goal's pop-up. */
  private openIntro(kind: 'sheet' | 'goal' = 'sheet'): void {
    this.intro.dataset.kind = kind;
    this.intro.dataset.open = 'true';
  }

  /** Closed: the goal's pop-up goes back where it came from first, unless less motion is asked for. */
  private closeIntro(): void {
    closeInfo();
    const animated =
      this.intro.dataset.kind === 'goal' &&
      this.intro.dataset.open === 'true' &&
      !matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!animated) {
      this.intro.dataset.open = 'false';
      return;
    }
    this.intro.dataset.open = 'closing';
    setTimeout(() => {
      // Opened again meanwhile: leave it open.
      if (this.intro.dataset.open === 'closing') this.intro.dataset.open = 'false';
    }, 220);
  }

  /** A new place, and the one sentence that says what it does to you. */
  private showBuildingIntro(building: BuildingDef, level: LevelDef): void {
    clear(this.introBody);
    this.introBody.append(
      el('div', { class: 'intro-head' }, [
        el('h3', { text: 'A new building' }),
        infoButton(building.name, [building.premise, ownLiftLine(building)]),
      ]),
      el('div', { class: 'intro-row intro-plate' }, [
        el('div', { class: 'intro-building', 'data-building': building.id }, [icon('home')]),
        el('div', {}, [
          el('div', { class: 'intro-title', text: building.name }),
          el('div', { class: 'goal-chips intro-chips' }, buildingChips(building)),
        ]),
      ]),
    );

    const go = el('button', { class: 'primary', type: 'button' }, ['Got it']);
    go.addEventListener('click', () => {
      this.data = markBuildingSeen(this.data, building.id);
      save(this.data);
      this.closeIntro();
      // Then whatever else is new here — traits, if any — and then the level.
      this.startLevel(level);
    });
    this.introBody.append(go);
    this.openIntro();
  }

  /**
   * The two ways to send the lift (ADR-0052): the last tap, as shift 2 taught,
   * or a few stops tapped ahead. A player asked for the second; the owner made
   * it a choice, said once in the guided shifts and kept in Settings.
   */
  private offerTapAhead(level: LevelDef): void {
    clear(this.introBody);
    const choose = (on: boolean) => {
      this.data = { ...this.data, tapAhead: on };
      save(this.data);
      this.closeIntro();
      this.startLevel(level);
    };
    const option = (title: string, line: string, on: boolean) => {
      const button = el('button', { class: 'choice', type: 'button' }, [
        el('div', { class: 'intro-title', text: title }),
        el('div', { class: 'intro-line', text: line }),
      ]);
      button.addEventListener('click', () => choose(on));
      return button;
    };
    this.introBody.append(
      el('h3', { text: 'Two ways to send the lift' }),
      el('div', { class: 'choices' }, [
        option(
          'Last tap',
          'The lift goes to the last floor you tap. Tap the next one once the doors open.',
          false,
        ),
        option(
          'Tap ahead',
          'Tap up to three floors and the lift goes to them in turn, while you plan the next. ' +
            'It can be quicker, and score more points. Tap a floor again to take it off; ' +
            'Clear stops forgets them all.',
          true,
        ),
      ]),
      el('div', { class: 'intro-line', text: 'You can change it any time in Settings.' }),
    );
    this.openIntro();
  }

  /**
   * Into the shift by lift (ADR-0057): from the building, a zoom into its floor
   * and a ride up from the street; to the next shift, one floor; a retry, the
   * doors alone. The shift is set up behind the closed doors, and its clock
   * starts once they are open.
   */
  private beginLevel(level: LevelDef): void {
    // A second Play while the lift is on its way waits for that one.
    if (this.doors.busy) return;
    const from = this.screen;
    const again = this.lastShift === level.id;
    const shift = shiftInBuilding(level.id);
    this.setPaused(false);
    // A level starts unheld, whatever was showing over the last one.
    this.held = false;
    this.coaching = null;
    this.meeting = null;
    this.pickingStairs = false;
    this.choosingPower = false;
    this.powerSaidAt = -1;
    this.powers.close();
    this.countdown.stop();
    this.entering = true;
    // A retry stays on its floor; the next shift is one floor up; from the street, all the way.
    const stops = again
      ? stopsUp(shift).slice(-1)
      : from === 'menu'
        ? stopsUp(shift)
        : stopsUp(shift).slice(-2);
    void this.doors
      .ride(
        {
          building: level.buildingId ?? 'office-tower',
          stops,
          going: 'up',
          into: from === 'menu' ? this.floorOnHome(level) : null,
          chime: () => this.sound.play('doors'),
          slide: (way) => this.sound.liftDoors(way),
        },
        async () => {
          this.show('game');
          this.music.play(trackFor(level.buildingId, shift));
          this.setUpShift(await this.ensurePixi(), level);
        },
      )
      .then(() => {
        this.entering = false;
        // The guided shifts teach at their first moment instead (ADR-0030).
        if (this.session?.level.id === level.id && !COACHED_LEVELS.includes(level.id)) {
          this.countdown.start();
        }
      });
  }

  /** Back to the building by lift (ADR-0057): down to the street, and out from the shift's floor. */
  private leaveShift(level: LevelDef | undefined): void {
    if (this.doors.busy) return;
    void this.doors.ride(
      {
        building: level?.buildingId ?? 'office-tower',
        stops: level ? stopsDown(shiftInBuilding(level.id)) : ['G'],
        going: 'down',
        outOf: () => (level ? this.floorOnHome(level) : null),
        chime: () => this.sound.play('doors'),
        slide: (way) => this.sound.liftDoors(way),
      },
      () => {
        this.show('menu');
        // The building centres its next floor a frame after it is drawn; measure after that.
        return new Promise<void>((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
        });
      },
    );
  }

  /** A shift's floor on the home screen, to zoom into or out from: its lift if parked there. */
  private floorOnHome(level: LevelDef): Zoom | null {
    const stage = this.menuScreen.querySelector<HTMLElement>('.home-stage');
    const floor = this.menuScreen.querySelector<HTMLElement>(`.floor[data-level="${level.id}"]`);
    if (!stage || !floor) return null;
    const target =
      floor.querySelector<HTMLElement>('.lift') ??
      floor.querySelector<HTMLElement>('.floor-shaft') ??
      floor;
    return { stage, target };
  }

  /** The shift itself, set up behind the closed doors. */
  private setUpShift(app: Application, level: LevelDef): void {
    this.lastShift = level.id;
    this.view?.destroy();
    // A puzzle is played on the building's first lift (ADR-0049); every other shift on yours.
    const owned = loadoutFor(this.data, liftOf(level).id);
    this.session = new Session(level, this.data.mode, 1, loadoutForShift(level, owned));
    this.soundWatch = null;
    // The lintel wears the building's colours in play (ADR-0026).
    this.stage.dataset.building = level.buildingId ?? 'office-tower';
    this.view = this.buildView(app, level);
    // A new view holds no reserve: measure the corner again.
    this.cornerKey = '';
    // A puzzle on the first lift (ADR-0049) read as parts gone missing (the
    // owner, PLAYTEST_LOG 2026-10-29): said in play too, beside the car.
    if (level.objective.kind === 'withinTrips' && !isBaseLoadout(owned)) {
      this.view.tell(
        'A puzzle: played on the first lift. Your parts wait in the garage.',
        level.elevator.startFloor,
        5000,
      );
    }

    // Only ever shown in select mode, which the game no longer offers. Kept
    // as one honest line rather than removed, because the sim still supports
    // the mode and half the replay corpus is recorded in it.
    this.closeButton.style.display = this.data.mode === 'select' ? '' : 'none';
    // The shift's brass plaque, as on the home screen, then its name: on a
    // narrow phone the name gives way and the number stays.
    clear(this.hudLevel);
    this.hudLevel.append(
      el('span', {
        class: 'plaque',
        text: String(shiftInBuilding(level.id)).padStart(2, '0'),
      }),
      el('span', { class: 'level-name', text: level.name }),
    );
    this.updateHud();
  }

  /** The shift's view, wired to send taps to whichever session is running. */
  private buildView(app: Application, level: LevelDef): ShaftView {
    const view = new ShaftView(app, level);
    // A command names its car only in a bank (ADR-0034), so a run with one
    // car records exactly what it always did.
    const onCar = (car: number) => (car === 0 ? {} : { car });
    // Tapping ahead (ADR-0052): each tap a stop, in order; else the last tap wins.
    const route = () => (this.data.tapAhead === true ? 'addStop' : 'callTo');
    view.onFloorTap = (floor, car) => {
      // Choosing who takes the stairs: a floor tap is a change of mind.
      if (this.pickingStairs) {
        this.stopPicking();
        return;
      }
      this.session?.send({ type: route(), floor, ...onCar(car) });
    };
    view.onPassengerTap = (passengerId, car) => {
      const session = this.session;
      if (!session) return;
      const passenger = session.state.passengers.find((p) => p.id === passengerId);
      if (session.state.mode === 'select') {
        // Into whichever car stands open on their floor.
        const open = carsOf(session.state).findIndex(
          (c) =>
            c.phase === 'servicing' &&
            c.positionMilli === (passenger?.standingFloor ?? -1) * MILLI_PER_FLOOR,
        );
        session.send({ type: 'board', passengerId, ...onCar(Math.max(0, open)) });
        return;
      }
      // A person is their floor: the lift goes there as if the floor had been
      // tapped. Who boards is the lift's to decide, in arrival order — the
      // owner took choosing out of play as too slow for the game (ADR-0032).
      if (!passenger) return;
      if (this.pickingStairs) {
        // Someone who can walk does; anyone else, and the lift is not sent.
        if (canTakeStairs(session.state, passenger)) {
          session.send({ type: 'usePower', power: 'stairs', passengerId });
          this.stopPicking();
        }
        return;
      }
      // Tapping ahead, a person already on the way is not a floor taken off again.
      const plan = planOf(session.state, carsOf(session.state)[car] ?? session.state.elevator);
      if (route() === 'addStop' && plan.includes(passenger.standingFloor)) return;
      session.send({ type: route(), floor: passenger.standingFloor, ...onCar(car) });
    };
    view.onCoachTap = (floor) => this.answerLesson(floor);
    view.resize(this.stage.clientWidth, this.stage.clientHeight);
    return view;
  }

  /**
   * The shift drawn anew from its state, after the WebGL context came back:
   * new texts are drawn and uploaded again. Nothing the sim holds is touched,
   * and what was on screen over it — a lesson, a newcomer's ring, the car a
   * bank had chosen — is put back.
   */
  private redrawView(): void {
    const app = this.pixi;
    const old = this.view;
    if (!app || !old || !this.session) return;
    const chosen = old.selectedCar;
    old.destroy();
    const view = this.buildView(app, this.session.level);
    view.selectedCar = chosen;
    if (this.coaching) view.setCoach(this.coaching);
    if (this.meeting) view.pointAt(this.meeting, Number.POSITIVE_INFINITY);
    this.view = view;
  }

  /**
   * Dev only: lose the WebGL context and get it back `afterMs` later, the way
   * a phone does, to see that a shift comes back drawn (performance review).
   */
  loseContext(afterMs = 500): void {
    if (!import.meta.env.DEV) return;
    const renderer = this.pixi?.renderer as { gl?: WebGLRenderingContext } | undefined;
    const lose = renderer?.gl?.getExtension('WEBGL_lose_context');
    if (!lose) return;
    lose.loseContext();
    setTimeout(() => lose.restoreContext(), afterMs);
  }

  /** Read-only peek at the running session. Used by dev tooling and browser checks. */
  get current(): Session | null {
    return this.session;
  }

  /** Drive the game the way a tap would, for automated checks. `car` picks a car in a bank. */
  tap(command: 'floor' | 'passenger' | 'close', value?: number | string, car = 0): void {
    if (command === 'floor')
      this.session?.send({ type: 'callTo', floor: Number(value), ...(car ? { car } : {}) });
    else if (command === 'passenger')
      this.session?.send({ type: 'board', passengerId: String(value) });
    else this.session?.send({ type: 'closeDoors' });
  }

  /** Power-ups show from the first shift without a lesson, and never on a puzzle. */
  private powersShown(session: Session): boolean {
    return session.state.power.allowed && !COACHED_LEVELS.includes(session.level.id);
  }

  /** The charge spent or kept: the choice, held over the shift (PLAYTEST_LOG 2026-10-29). */
  private openPowers(): void {
    const session = this.session;
    if (!session || !this.powersShown(session) || this.paused || this.held) return;
    this.choosingPower = true;
    const car = carsOf(session.state)[this.view?.selectedCar ?? 0] ?? session.state.elevator;
    this.powers.open(session.state, car);
  }

  private closePowers(): void {
    this.powers.close();
    this.choosingPower = false;
  }

  /**
   * Spend the charge (ADR-0055): at once, as the shift goes on, or for the
   * stairs on who is tapped next, with the shift still waiting.
   */
  private usePower(name: PowerName): void {
    const session = this.session;
    if (!session) return;
    if (name === 'stairs') {
      this.pickingStairs = true;
      if (this.view) this.view.stairsPick = true;
      return;
    }
    const car = this.view?.selectedCar ?? 0;
    session.send({ type: 'usePower', power: name, ...(car === 0 ? {} : { car }) });
    this.choosingPower = false;
  }

  private stopPicking(): void {
    this.pickingStairs = false;
    this.choosingPower = false;
    if (this.view) this.view.stairsPick = false;
  }

  /** What a power just did, said by the car once. */
  private sayPower(session: Session): void {
    const last = session.state.power.last;
    if (!last || last.atMs === this.powerSaidAt) return;
    this.powerSaidAt = last.atMs;
    const car = carsOf(session.state)[this.view?.selectedCar ?? 0] ?? session.state.elevator;
    this.view?.tell(POWER_DONE[last.name], Math.round(car.positionMilli / MILLI_PER_FLOOR), 2500);
  }

  /**
   * The first charge (ADR-0055): stop, say what the meter is for and what the
   * four do, once a save. The meter fills before the player knows it is there,
   * so the card comes with something to spend.
   */
  private introducePowers(session: Session): void {
    if (this.data.metPowers || !this.powersShown(session) || session.state.power.charges < 1) {
      return;
    }
    this.held = true;
    clear(this.introBody);
    const how = `${POWER.meterFull} people home in a row, with nobody lost, fill the ring beside Pause. A full ring is one power-up: tap it, and the shift waits while you choose.`;
    this.introBody.append(
      el('div', { class: 'intro-head' }, [
        el('h3', { text: 'A power-up' }),
        infoButton('Power-ups', [
          how,
          ...POWER_NAMES.map((name) => `${POWER_WORDS[name].title}: ${POWER_WORDS[name].line}`),
        ]),
      ]),
      el('div', { class: 'goal-chips intro-chips' }, [
        chip('person', `${POWER.meterFull} in a row`),
        chip('power', '1 power-up'),
      ]),
      el(
        'div',
        { class: 'power-list' },
        POWER_NAMES.map((name) =>
          el('div', { class: 'power-row' }, [
            powerBadge(name),
            el('div', { class: 'intro-title', text: POWER_WORDS[name].title }),
          ]),
        ),
      ),
    );
    const go = el('button', { class: 'primary', type: 'button' }, ['Got it']);
    go.addEventListener('click', () => {
      this.data = { ...this.data, metPowers: true };
      save(this.data);
      this.closeIntro();
      this.held = false;
    });
    this.introBody.append(go);
    this.openIntro();
  }

  /**
   * The first time anyone new appears (ADR-0036; the crews since ADR-0029):
   * stop, show them as the game draws them, say what they do and what to do,
   * and carry on once it has been read — pointing them out on their floor.
   */
  private introduceSomeone(session: Session): void {
    const met = nextToMeet(session.state, this.data.seenArchetypes);
    const intro = met ? INTRODUCTIONS[met.key] : undefined;
    if (!met || !intro) return;
    const { passenger } = met;
    this.held = true;
    clear(this.introBody);
    const ground = groundOf(session.level.buildingId);
    // A group is shown together, as it boards: up to three of them.
    const shown =
      passenger.groupId === null
        ? [passenger]
        : session.state.passengers
            .filter(
              (p) =>
                p.groupId === passenger.groupId &&
                p.state === 'waiting' &&
                p.standingFloor === passenger.standingFloor,
            )
            .slice(0, 3);
    const people = el('div', { class: 'intro-crew' });
    // The ticket as it reads in play: a postman's whole round (ADR-0031).
    const round = (p: Passenger) =>
      p.errands.length > 0
        ? [...p.errands, p.destination].map((f) => floorName(f, ground)).join('·')
        : undefined;
    people.innerHTML = shown
      .map((p) =>
        personSvg(p.id, p, p.destination, alightFloor(session.state, p), ground, round(p)),
      )
      .join('');
    this.introBody.append(
      el('div', { class: 'intro-head' }, [
        el('h3', { text: intro.heading }),
        infoButton(intro.title, [intro.why ?? '', intro.what]),
      ]),
      el('div', { class: 'intro-row intro-plate' }, [
        people,
        el('div', {}, [
          el('div', { class: 'intro-title', text: intro.title }),
          el('div', { class: 'intro-line intro-todo', text: intro.todo }),
        ]),
      ]),
    );
    const go = el('button', { class: 'primary', type: 'button' }, ['Got it']);
    go.addEventListener('click', () => {
      this.data = markArchetypesSeen(this.data, introduces(met.key));
      save(this.data);
      this.closeIntro();
      delete this.intro.dataset.at;
      this.held = false;
      this.meeting = null;
      this.view?.pointAt(passenger.id);
    });
    this.introBody.append(go);
    // Out of their way: someone low in the building gets the card at the top,
    // and the ring finds them while it is read.
    if (passenger.standingFloor < session.level.floors / 2) this.intro.dataset.at = 'top';
    this.meeting = passenger.id;
    this.view?.pointAt(passenger.id, Number.POSITIVE_INFINITY);
    this.openIntro();
  }

  private endSession(): void {
    this.paused = false;
    this.held = false;
    this.coaching = null;
    this.meeting = null;
    this.pauseSheet.dataset.open = 'false';
    this.countdown.stop();
    this.view?.destroy();
    this.view = null;
    this.session = null;
  }

  private frame(deltaMs: number): void {
    const session = this.session;
    if (!session || this.screen !== 'game') return;

    if (this.paused || this.held || this.entering || this.choosingPower) {
      // The clock stops but the picture does not: animation keeps running so the
      // paused frame still looks alive behind the menu, or behind the lift's doors.
      session.tickPresentation(deltaMs);
      this.view?.update(session, deltaMs);
      // Choosing who walks, the banner and the button still answer.
      if (this.choosingPower) this.syncControls(session);
      return;
    }

    // The count into the shift: in sight and still, the clock not yet started.
    if (this.countdown.advance(deltaMs)) {
      session.tickPresentation(deltaMs);
      this.view?.update(session, deltaMs);
      return;
    }

    // Before the clock moves: a lesson is about this exact moment (ADR-0030).
    if (this.teach(session)) {
      session.tickPresentation(deltaMs);
      this.view?.update(session, deltaMs);
      return;
    }
    session.advance(deltaMs);
    this.introduceSomeone(session);
    this.introducePowers(session);
    this.sayPower(session);
    for (const event of soundEvents(this.soundWatch, session.state)) this.sound.play(event);
    this.soundWatch = watch(session.state);
    this.view?.update(session, deltaMs);
    this.updateHud();
    this.music.setHurry(this.hurrying(session));

    if (session.finished) this.finish(session);
  }

  /** A shift's clock in its last ten seconds: the music hurries with it (ADR-0056). */
  private hurrying(session: Session): boolean {
    const { state, level } = session;
    if (session.finished || level.objective.kind === 'withinTrips') return false;
    const clock = clockFor(state.elapsedMs, state.timeLimitMs, level.objective);
    return clock.remainingMs < LAST_SECONDS_MS;
  }

  /**
   * The bonus goal, under the lintel (ADR-0040): what it asks, and the moment
   * it is out of reach. A trip count shows the trips so far against it.
   */
  private showBonus(session: Session): void {
    const bonus = session.level.bonus;
    setHidden(this.hudBonus, !bonus);
    if (!bonus) return;
    const status = bonusStatus(session.state, bonus);
    setText(
      this.hudBonusText,
      bonus.kind === 'withinTrips'
        ? `${session.state.score.trips}/${bonus.trips} trips`
        : bonusText(bonus, session.level),
    );
    setData(this.hudBonus, 'state', status.missed ? 'missed' : status.met ? 'met' : 'on');
  }

  /**
   * What the clock window shows: the time left, or on a puzzle shift, which
   * has no clock to beat (ADR-0037), the trips left in its place.
   */
  private clockWindow(session: Session): { value: string; label: string; urgency: string } {
    const { state, level } = session;
    const status = objectiveStatus(state, level.objective);
    if (status.kind === 'deliveries' && status.tripsLeft !== null) {
      const left = status.tripsLeft;
      return {
        value: String(left),
        label: left === 1 ? 'trip left' : 'trips left',
        urgency: left <= 1 ? 'critical' : left <= 2 ? 'low' : 'calm',
      };
    }
    // A hold-out held, with riders still to set down: it is won, and a red 0:00
    // read as the time running out (the owner, PLAYTEST_LOG 2026-10-28).
    if (status.kind === 'shift' && status.remainingMs === 0 && status.lost <= status.allowed) {
      return { value: 'Held', label: 'shift', urgency: 'calm' };
    }
    // Counts down to whatever ends the shift — the level's limit, or a shift's end.
    const clock = clockFor(state.elapsedMs, state.timeLimitMs, level.objective);
    return { value: formatDuration(clock.remainingMs), label: clock.label, urgency: clock.urgency };
  }

  /** Every frame; each text and attribute is written only when it changes (ui/write.ts). */
  private updateHud(): void {
    const session = this.session;
    if (!session) return;
    const state = session.state;

    const graded = state.stars.metric ?? 'time';
    const clock = this.clockWindow(session);
    setText(this.hudTime, clock.value);
    setData(this.hudTimeStat, 'urgency', clock.urgency);
    setText(this.hudTimeLabel, clock.label);
    // A time-graded level shows its stars going out instead of a label: the
    // clock's own colour now belongs to the time left.
    const timed = graded === 'time';
    setHidden(this.hudTimeLabel, timed);
    setHidden(this.hudPips, !timed);
    if (timed) {
      const { three, two } = session.level.stars;
      const reach = starsInReach(state.elapsedMs, three, two);
      this.pips.forEach((pip, i) => {
        setData(pip, 'on', String(i < reach));
      });
    }
    const objective = session.level.objective;
    this.showBonus(session);

    // What the level actually grades, not how many people exist. Showing the
    // roster made a perfect run read as "13 of 15" — two people apparently
    // dropped — when 13 was the target all along.
    const status = objectiveStatus(state, session.level.objective);
    if (status.kind === 'shift') {
      // The shift's time is on the clock; this is what it allows to be lost.
      setText(
        this.hudDelivered,
        status.allowed > 0 ? `${status.lost}/${status.allowed}` : String(status.lost),
      );
      setText(this.hudDeliveredLabel, 'lost');
    } else {
      setText(this.hudDelivered, `${status.done}/${status.needed}`);
      setText(this.hudDeliveredLabel, 'moved');
    }

    // Only show the score where it is what the level grades on. On a v0.1 level
    // it would be a number the player has no use for.
    setShown(this.hudScoreStat, graded === 'service');
    setText(this.hudScore, String(Math.round(state.score.service)));
    setText(
      this.hudScoreLabel,
      objective.kind === 'serviceAbove' ? `need ${objective.minService}` : 'points',
    );

    // The score keeps its star-pace colour; the clock's colour is the time left.
    if (graded === 'service') {
      const { three, two } = session.level.stars;
      const service = state.score.service;
      setData(
        this.hudScoreStat,
        'pace',
        service >= three ? 'three' : service >= two ? 'two' : 'one',
      );
    }

    this.syncControls(session);
  }

  /** The buttons over the shaft, for the chosen car. */
  private syncControls(session: Session): void {
    const state = session.state;
    const car = carsOf(state)[this.view?.selectedCar ?? 0] ?? state.elevator;
    // Only ever shown in select mode, and only while there is something to close
    // the doors on. A button that appears and vanishes on its own is worse than
    // no button, which is what the standing prompt line used to be.
    const canClose = state.mode === 'select' && session.servicingFloor !== null;
    setShown(this.closeButton, canClose);
    // Power-ups (ADR-0055). A choice of who walks ends when nobody can.
    if (this.pickingStairs && !powerApplies(state, 'stairs', car)) this.stopPicking();
    const powers = this.powersShown(session);
    this.powers.sync(state, powers, this.pickingStairs);
    // Tapping ahead: always there, so it never jumps out from under a thumb.
    const tapAhead = this.data.tapAhead === true;
    setShown(this.clearButton, tapAhead);
    if (tapAhead) {
      const nothing = planOf(state, car).length === 0;
      if (this.clearButton.disabled !== nothing) this.clearButton.disabled = nothing;
    }
    this.keepCornerClear(tapAhead);
  }

  /** The lowest rows end short of Clear stops in their corner. Measured when it changes. */
  private keepCornerClear(tapAhead: boolean): void {
    const key = `${tapAhead}|${this.stage.clientWidth}|${this.stage.clientHeight}`;
    if (!this.view || key === this.cornerKey) return;
    this.cornerKey = key;
    if (!tapAhead) {
      this.view.corner = { width: 0, height: 0 };
      return;
    }
    const stage = this.stage.getBoundingClientRect();
    const box = this.clearButton.getBoundingClientRect();
    this.view.corner = { width: stage.right - box.left, height: stage.bottom - box.top };
  }

  /**
   * The guided first shifts (ADR-0030): if this moment is one a lesson is
   * about, stop the clock and show it. True while one is showing.
   */
  private teach(session: Session): boolean {
    if (!COACHED_LEVELS.includes(session.level.id)) return false;
    const ground = groundOf(session.level.buildingId);
    const lesson = lessonFor(
      session.level.id,
      session.state,
      this.data.coached,
      (floor) => floorName(floor, ground),
      this.data.tapAhead === true,
    );
    if (!lesson) return false;
    this.coaching = lesson;
    this.held = true;
    this.view?.setCoach(lesson);
    return true;
  }

  /** A lesson answered: remember it, carry on, and send the tap it asked for. */
  private answerLesson(floor: number | null): void {
    const lesson = this.coaching;
    if (!lesson) return;
    this.data = { ...this.data, coached: [...new Set([...this.data.coached, lesson.id])] };
    save(this.data);
    this.coaching = null;
    this.held = false;
    this.view?.setCoach(null);
    if (floor !== null) {
      this.session?.send({ type: this.data.tapAhead === true ? 'addStop' : 'callTo', floor });
    }
  }

  private finish(session: Session): void {
    const summary = session.summary;
    const stars = starsFor(session.state);

    // Paid on improvement only, so a solved level cannot be farmed for parts.
    const previousStars = this.data.records[session.level.id]?.stars ?? 0;
    // At the building's own pay (ADR-0027): a bigger building's lift costs more.
    // A bonus goal pays once, the first time it is met (ADR-0040).
    const bonus = session.level.bonus;
    const bonusMet = bonus !== undefined && bonusStatus(session.state, bonus).met;
    const firstBonus = bonusMet && !this.data.records[session.level.id]?.bonus;
    const bonusCoins = bonusPayFor(firstBonus, session.level.payout);
    const earned = payoutFor(previousStars, stars, session.level.payout) + bonusCoins;
    this.lastEarned = earned;
    this.lastBonus = bonus
      ? { text: bonusText(bonus, session.level), met: bonusMet, coins: bonusCoins }
      : null;
    this.data = { ...this.data, coins: this.data.coins + earned };

    const graded = summary.graded;
    this.lastMilestone = runMilestone(
      this.data.records[session.level.id],
      graded,
      stars,
      summary.metric,
    );
    this.data = recordRun(this.data, session.level.id, graded, stars, summary.metric, bonusMet);
    this.lastNudge = this.nudgeFor(session, stars, summary.metric);
    const replay = this.inSitting(session.toReplay(`${session.level.name} — ${session.mode}`));
    this.data = addReplay(this.data, replay);
    save(this.data);
    // After saving, never before: the local copy is the one that matters, and
    // the upload is a convenience on top of it.
    collectReplay(replay);

    this.renderResults(session, stars);
    this.endSession();
    this.show('results');
    if (this.lastNudge?.lesson) {
      // After the stars have landed: the lesson is the next thing, not the first.
      const wait = matchMedia('(prefers-reduced-motion: reduce)').matches ? 300 : 1800;
      const upgrade = this.resultsFoot.querySelector<HTMLElement>('.brass-pill');
      this.callToGarage(upgrade, liftOf(session.level), wait);
    }
  }

  /**
   * The garage, pointed at from the results (ADR-0041): the first part, the
   * first time one is in reach; then, where a player is stuck, a better lift —
   * only ever when a part is affordable, so the button helps.
   */
  private nudgeFor(
    session: Session,
    stars: number,
    metric: RunSummary['metric'],
  ): { text: string; lesson: boolean } | null {
    const lift = liftOf(session.level);
    const loadout = loadoutFor(this.data, lift.id);
    const { records, coins } = this.data;
    if (this.lessonDue(loadout, lift)) return { text: '', lesson: true };
    // Parts do not count on a puzzle (ADR-0049): a better lift is no answer to it.
    if (loadoutForShift(session.level, loadout) !== loadout) return null;
    const affordable = shelf({ coins, loadout }, lift.parts).some((offer) => offer.affordable);
    const hint = upgradeHint({ stars, metric }, records[session.level.id], affordable);
    return hint ? { text: hint, lesson: false } : null;
  }

  /** A building's last shift, won for the first time: it is done (PLAYTEST_LOG, 2026-10-28). */
  private buildingDone(level: LevelDef, upcoming: LevelDef | null): BuildingDone | null {
    const here = level.buildingId ?? 'office-tower';
    const mates = LEVELS.filter((l) => (l.buildingId ?? 'office-tower') === here);
    if (this.lastMilestone !== 'first-clear' || mates[mates.length - 1]?.id !== level.id) {
      return null;
    }
    return {
      building: getBuilding(here).name,
      stars: mates.reduce((sum, l) => sum + (this.data.records[l.id]?.stars ?? 0), 0),
      of: mates.length * 3,
      next: upcoming?.buildingId ? getBuilding(upcoming.buildingId).name : null,
    };
  }

  private renderResults(session: Session, stars: number): void {
    const summary = session.summary;
    const level = session.level;
    const won = summary.status === 'won';
    const onService = summary.metric === 'service';

    // The graded number and its targets live on the score card's bar now, so
    // the tiles carry everything else.
    // The same count the HUD and the pause card showed: against what the level
    // needed, not against everyone who came. An AI tester read "0/5" in play and
    // "0 of 7" here as two different games.
    const status = objectiveStatus(session.state, level.objective);
    const tiles: [string, string][] = [
      [
        'Moved',
        status.kind === 'deliveries'
          ? `${status.done}/${status.needed}`
          : String(summary.delivered),
      ],
    ];
    if (status.kind === 'deliveries' && summary.totalPassengers > status.needed) {
      tiles.push(['People', String(summary.totalPassengers)]);
    }
    if (onService || !won) tiles.push(['Time', formatDuration(summary.elapsedMs)]);
    if (onService && !won) tiles.push(['Points', String(summary.service)]);
    // A trip is the lift setting off, the unit puzzles and trip bonuses count in.
    // Showing door stops beside a "12 trips" bonus left it uncheckable.
    const countsTrips = level.stars.metric === 'trips' || level.bonus?.kind === 'withinTrips';
    tiles.push(
      countsTrips
        ? ['Trips', String(session.state.score.trips)]
        : ['Stops', String(summary.doorCycles)],
    );
    tiles.push(['Floors', String(summary.floorsTravelled)]);
    tiles.push(['Avg wait', formatDuration(summary.avgWaitMs)]);

    // Trait tiles appear only when the level actually used the trait, so the
    // breakdown stays as short as the level was complicated. A guest turned
    // away at a full floor did not give up: the sim keeps them apart.
    const gaveUp = gaveUpWaiting(summary);
    if (gaveUp > 0) tiles.push(['Gave up', String(gaveUp)]);
    if (summary.turnedAway > 0) tiles.push(['Turned away', String(summary.turnedAway)]);
    if (summary.couriersOnTime + summary.couriersLate > 0) {
      tiles.push([
        'On time',
        `${summary.couriersOnTime} of ${summary.couriersOnTime + summary.couriersLate}`,
      ]);
    }
    if (summary.vipsServed > 0) tiles.push(['VIPs served', String(summary.vipsServed)]);
    if (!isBaseLoadout(session.state.loadout)) {
      tiles.push([
        'Lift',
        UPGRADE_SYSTEMS.filter((s2) => (session.state.loadout[s2] ?? 0) > 0)
          .map((s2) => tierOf(s2, session.state.loadout, liftOf(level).parts).name)
          .join(', '),
      ]);
    }

    const number = String(shiftInBuilding(level.id)).padStart(2, '0');
    const building = level.buildingId ? getBuilding(level.buildingId).name : null;
    const upcoming = won ? nextLevel(level.id) : null;
    const complete = won ? this.buildingDone(level, upcoming) : null;

    renderResults(
      this.resultsBody,
      this.resultsFoot,
      {
        shift: building ? `Shift ${number} · ${building}` : `Shift ${number}`,
        name: level.name,
        won,
        stars,
        metric: summary.metric,
        value: summary.graded,
        two: level.stars.two,
        three: level.stars.three,
        reason: explainOutcome(summary, level, stars, lostIn(session.state)),
        cause: won ? null : lossCause(summary, level, lostIn(session.state)),
        tiles,
        earned: this.lastEarned,
        milestone: this.lastMilestone,
        bonus: this.lastBonus,
        nudge: this.lastNudge,
        complete,
      },
      {
        retry: () => this.startLevel(level),
        levels: () => this.leaveShift(level),
        garage: () => this.openGarage('results', level.buildingId),
        next: upcoming ? () => this.startLevel(upcoming) : null,
        starLanded: (index) => this.sound.star(index),
      },
    );
  }

  // ------------------------------------------------------------------ export

  private exportReplays(): void {
    const blob = new Blob([JSON.stringify(this.data.replays, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = el('a', {
      href: url,
      download:
        'full-elevator-replays-' +
        new Date().toISOString().slice(0, 19).replace(/:/g, '') +
        '.json',
    });
    link.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  }
}
